import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { paymentMethodsTooltip } from "../../../constants/TooltipInformation";
import {
  validateIntegerLength,
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import {
  paymentTypeList,
  phoneRestrictionsList,
} from "../../../constants/Payment";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { payloadConstants } from "../../../constants/defaultValues";
import { removeSampleTestArray } from "../../actions/cleanup";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const PaymentInformation = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [paymentCode, setPaymentCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [paymentName, setPaymentName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [paymentType, setPaymentType] = useState<string | null>(null);
  const [paymentTypeError, setPaymentTypeError] = useState<boolean>(false);
  const [postTypeList, setPostTypeList] = useState<string[]>([]);
  const [postTypeNumber, setPostTypeNumber] = useState<string | null>(null);
  const [specialGuarentee, setSpecialGuarentee] = useState<boolean>(false);
  const [creditLimit, setCreditLimit] = useState<number | null>(null);
  const [creditError, setCreditError] = useState<boolean>(false);
  const [activeInterface, setActiveInterface] = useState<boolean>(false);
  const [posChanges, setPosChanges] = useState<boolean>(false);
  const [allMovies, setAllMovies] = useState<boolean>(false);
  const [allowPost, setAllowPost] = useState<boolean>(false);
  const [centralReservation, setCentralReservation] = useState<string | null>(
    null,
  );
  const [reservationError, setReservationError] = useState<boolean>(false);
  const [phoneRestrictions, setPhoneRestrictions] = useState<number | null>(
    null,
  );
  const [paymentInformation, setPaymentInformation] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const paymentPayload = {
    code: paymentCode,
    name: paymentName,
    paymentType: paymentType,
    postTypeNumber:
      postTypeNumber ??
      payloadConstants[RoomPropertyEnum.PaymentMethods].POST_TYPE_NUMBER,
    isUseSpecialGuarentee: specialGuarentee ?? false,
    creditLimit:
      creditLimit ??
      payloadConstants[RoomPropertyEnum.PaymentMethods].CREDIT_LIMIT,
    isActivateInterface: activeInterface,
    isRestrictPOSChanges: posChanges,
    isMovieRestrictions: allMovies,
    isInterfacePostToCheckoutFolio: allowPost,
    centralResvEquivalent:
      centralReservation ??
      payloadConstants[RoomPropertyEnum.PaymentMethods].CENTRAL_RESERVATION,
    phoneRestrictions:
      phoneRestrictions ??
      payloadConstants[RoomPropertyEnum.PaymentMethods].PHONE_RESTRICTIONS,
    isActive: active ?? true,
  };

  const addPaymentInformation = () => {
    if (validateInput()) {
      if (!paymentCode || paymentCode === null || paymentCode === "") {
        setCodeError(true);
      }
      if (!paymentName || paymentName === null || paymentName === "") {
        setNameError(true);
      }
      if (!paymentType || paymentType === null || paymentType === "") {
        setPaymentTypeError(true);
      } else if (
        checkDuplicate(
          Array.isArray(paymentInformation) ? paymentInformation : [],
          DNDFieldsEnum.PAYMENTCODE,
          paymentCode,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setPaymentInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...paymentPayload,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...paymentPayload,
                  },
                ],
          );
        });
        setLastUpdatedRecord(paymentPayload);
        editPaymentInformation(null);
        setEditClicked(false);
      }
    }
  };

  const editPaymentInformation = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setPaymentCode(list?.code);
    setPaymentName(list?.name);
    setPaymentType(list?.paymentType);
    setPostTypeNumber(list?.postTypeNumber);
    setSpecialGuarentee(list ? list.isUseSpecialGuarentee : false);
    setCreditLimit(list?.creditLimit);
    setActiveInterface(list ? list.isActivateInterface : false);
    setPosChanges(list ? list.isRestrictPOSChanges : false);
    setAllMovies(list ? list.isMovieRestrictions : false);
    setAllowPost(list ? list.isInterfacePostToCheckoutFolio : false);
    setCentralReservation(list?.centralResvEquivalent);
    setPhoneRestrictions(list?.phoneRestrictions);
    setActive(list ? list.isActive : true);
  };

  const updatePaymentInformation = () => {
    if (!validateInput()) return;
    if (!paymentCode || paymentCode === null || paymentCode === "") {
      setCodeError(true);
      return;
    }
    if (!paymentName || paymentName === null || paymentName === "") {
      setNameError(true);
      return;
    }
    if (!paymentType || paymentType === null || paymentType === "") {
      setPaymentTypeError(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(paymentInformation) ? paymentInformation : []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.PAYMENTCODE,
        paymentCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setPaymentInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...paymentPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editPaymentInformation(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.PaymentMethods} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deletePaymentInformation = (id: number) => {
    setPaymentInformation(deleteItem(paymentInformation, id));
  };

  const validateInput = () => {
    setCodeError(false);
    setNameError(false);
    setCreditError(false);
    setReservationError(false);
    let isValid = true;
    if (
      paymentCode &&
      !validateString(
        paymentCode,
        validationLength.paymentCode,
        validationType.CODE,
      )
    ) {
      setCodeError(true);
      isValid = false;
    }
    if (
      paymentName &&
      !validateString(
        paymentName,
        validationLength.paymentName,
        validationType.OTHER,
      )
    ) {
      setNameError(true);
      isValid = false;
    }
    if (
      creditLimit &&
      !validateIntegerLength(creditLimit, validationLength.paymentCreditLimit)
    ) {
      setCreditError(true);
      isValid = false;
    }
    if (paymentType || paymentType !== null || paymentType !== "") {
      setPaymentTypeError(false);
    }
    if (
      centralReservation &&
      !validateString(
        centralReservation,
        validationLength.centralReservation,
        validationType.OTHER,
      )
    ) {
      setReservationError(true);
      isValid = false;
    }
    return isValid;
  };

  useEffect(() => {
    data(paymentInformation);
    editClicked &&
      setSelectedId(
        checkData(paymentInformation, selectedId) ? selectedId : null,
      );
  }, [paymentInformation]);

  useEffect(() => {
    validateInput();
  }, [paymentCode, paymentName, creditLimit, centralReservation]);

  useEffect(() => {
    if (fieldValues) {
      setPaymentInformation(
        Array.isArray(fieldValues[RoomPropertyEnum.PaymentMethods])
          ? fieldValues[RoomPropertyEnum.PaymentMethods]
          : fieldValues[RoomPropertyEnum.PaymentMethods]
            ? [fieldValues[RoomPropertyEnum.PaymentMethods]]
            : null,
      );
      setPostTypeList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.postTypes],
          DNDFieldsEnum.POSTTYPECODE,
          DNDFieldsEnum.POSTTYPENAME,
        ),
      );
    }
  }, []);

  const paymentCodeHelperText = codeError
    ? !(paymentCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.paymentInformation.paymentCode
    : versaFieldHelperText.paymentInformation.paymentCode;

  const paymentNameHelperText = nameError
    ? !(paymentName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.paymentInformation.paymentName
    : versaFieldHelperText.paymentInformation.paymentName;

  const creditLimitHelperText = creditError
    ? versaFieldErrors.paymentInformation.creditLimit
    : versaFieldHelperText.paymentInformation.creditLimit;

  const centralReservationHelperText = reservationError
    ? versaFieldErrors.paymentInformation.centralReservation
    : versaFieldHelperText.paymentInformation.centralReservation;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Payment Information
              <Tooltip title={tooltipComponent(paymentMethodsTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editPaymentInformation(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editPaymentInformation(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Payment Code
                <Tooltip
                  title={tooltipComponent(
                    paymentMethodsTooltip.paymentMethodCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="paymentCode"
                variant="outlined"
                placeholder="Payment Code"
                value={paymentCode ?? ""}
                error={codeError}
                sx={{ width: "100%" }}
                helperText={paymentCodeHelperText}
                onChange={(e: any) => {
                  setPaymentCode(e.target.value.toUpperCase());
                  setCodeError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Payment Name
                <Tooltip
                  title={tooltipComponent(
                    paymentMethodsTooltip.paymentMethodName,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="paymentName"
                variant="outlined"
                placeholder="Payment Name"
                value={paymentName ?? ""}
                error={nameError}
                sx={{ width: "100%" }}
                helperText={paymentNameHelperText}
                onChange={(e: any) => {
                  setPaymentName(e.target.value);
                  setNameError(false);
                }}
              />
            </Stack>
          </Stack>

          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Payment Method Type
                <Tooltip
                  title={tooltipComponent(paymentMethodsTooltip.paymentType)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Select
                id="paymentMethodType"
                value={paymentType ?? ""}
                error={paymentTypeError}
                onChange={(e: any) => setPaymentType(e.target.value)}
                sx={{ width: "100%" }}
              >
                {paymentTypeList.map((payment, index) => (
                  <MenuItem value={payment} key={index}>
                    {payment}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Post Type Number
                <Tooltip
                  title={tooltipComponent(paymentMethodsTooltip.postTypeNumber)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                id="postTypeNumber"
                disablePortal
                options={removeSampleTestArray(postTypeList)}
                sx={{ width: "100%" }}
                disabled={!postTypeList}
                value={postTypeNumber ?? ""}
                onChange={(e: any) => setPostTypeNumber(e.target.innerText)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Post type number" />
                )}
              />
            </Stack>
          </Stack>

          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Credit Limit
                <Tooltip
                  title={tooltipComponent(paymentMethodsTooltip.creditLimit)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="creditLimit"
                variant="outlined"
                placeholder="Credit Limit"
                type="number"
                value={creditLimit ?? ""}
                error={creditError}
                sx={{ width: "100%" }}
                helperText={creditLimitHelperText}
                onChange={(e: any) => {
                  setCreditLimit(e.target.value >= 0 ? e.target.value : null);
                  setCreditError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Central Reservation Equivalent
                <Tooltip
                  title={tooltipComponent(
                    paymentMethodsTooltip.centralReservationEquivalent,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="centralReservationEquivalent"
                variant="outlined"
                placeholder="Central Reservation Equivalent ?"
                value={centralReservation ?? ""}
                error={reservationError}
                sx={{ width: "100%" }}
                helperText={centralReservationHelperText}
                onChange={(e: any) => {
                  setCentralReservation(e.target.value);
                  setReservationError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Phone Restrictions
                <Tooltip
                  title={tooltipComponent(
                    paymentMethodsTooltip.phoneRestrictions,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Select
                id="phoneRestrictions"
                value={phoneRestrictions ?? ""}
                onChange={(e: any) => setPhoneRestrictions(e.target.value)}
                sx={{ width: "100%" }}
              >
                {phoneRestrictionsList.map((phone, index) => (
                  <MenuItem value={index} key={index}>
                    {phone}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>

          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
            flexWrap="wrap"
            mt={4}
          >
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="useSpecialGuarantee"
                    checked={specialGuarentee}
                    onChange={(e: any) => setSpecialGuarentee(e.target.checked)}
                  />
                }
                label="Use special guarentee ?"
              />
              <Tooltip
                title={tooltipComponent(
                  paymentMethodsTooltip.useSpecialGuarantee,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="activeInterface"
                    checked={activeInterface}
                    onChange={(e: any) => setActiveInterface(e.target.checked)}
                  />
                }
                label="Active Interface ?"
              />
              <Tooltip
                title={tooltipComponent(paymentMethodsTooltip.activeInterface)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="restrictPOSChanges"
                    checked={posChanges}
                    onChange={(e: any) => setPosChanges(e.target.checked)}
                  />
                }
                label="Restrict POS Changes ?"
              />
              <Tooltip
                title={tooltipComponent(
                  paymentMethodsTooltip.restrictPOSCharges,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="allowAllMovies"
                    checked={allMovies}
                    onChange={(e: any) => setAllMovies(e.target.checked)}
                  />
                }
                label="Allow all movies ?"
              />
              <Tooltip
                title={tooltipComponent(paymentMethodsTooltip.allowAllMovies)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="allowPostToCheckOutFolio"
                    checked={allowPost}
                    onChange={(e: any) => setAllowPost(e.target.checked)}
                  />
                }
                label="Allow Post to check out folio ?"
              />
              <Tooltip
                title={tooltipComponent(
                  paymentMethodsTooltip.allowPostToCheckOut,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            <Tooltip title={tooltipComponent(paymentMethodsTooltip.active)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId ? updatePaymentInformation : addPaymentInformation
              }
              id="addNewPaymentInformation"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Payment
              Information
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Payment Information Available</Typography>
            <Stack direction="column" gap="10px">
              {paymentInformation ? (
                <DraggableItemList
                  itemData={paymentInformation}
                  onOrderUpdate={(order) => setPaymentInformation(order)}
                  onEdit={(info) => {
                    editPaymentInformation(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deletePaymentInformation(id)}
                  code={DNDFieldsEnum.PAYMENTCODE}
                  name={DNDFieldsEnum.PAYMENTNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Payment Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default PaymentInformation;
